import * as THREE from 'three';
import { physicsWorld, getRapier } from './physics.js';
import { config } from './config.js';
import { rigidBodies } from './objects-shared.js';

export const imageObjects = [];

const imageConfigs = [

    { 
        file: 'g1.svg', 
        width: 33, 
        height: 75, 
        depth: 10,
        pos: [-60, 50, -80],
        rot: [0, 0, 0],
        emissiveColor: 0x00ffff,
        emissiveIntensityNormal: 0.1
    },

    { 
        file: 'g2.svg', 
        width: 100, 
        height: 110, 
        depth: 10,
        pos: [-160, 120, -50],
        rot: [0, 0, 0],
        emissiveColor: 0xff00ff,
        emissiveIntensityNormal: 0.1
    },

    { 
        file: 'pol.svg', 
        width: 75, 
        height: 75, 
        depth: 5,
        pos: [160, 75, 10],
        rot: [0, -Math.PI/4, 0],
        emissiveColor: 0xffff00,
        emissiveIntensityNormal: 0.1
    },

    { 
        file: '1500xdym.jpg', 
        width: 30, 
        height: 30, 
        depth: 10,
        pos: [-20, 50, -100],
        rot: [0, 0, 0],
        emissiveColor: 0x00ff00,
        emissiveIntensityNormal: 0
    },
    { 
        file: 'hrd17.png', 
        width: 140, 
        height: 80, 
        depth: 10,
        pos: [-140, 40, -120],
        rot: [0, 0, 0],
        emissiveColor: 0x0088ff,
        emissiveIntensityNormal: 0
    },
    { 
        file: 'sny.svg', 
        width: 10, 
        height: 10, 
        depth: 5,
        pos: [-15, 30, -90],
        rot: [0, 0, 0],
        emissiveColor: 0xffffff,
        emissiveIntensityNormal: 0.2
    },

];

function createImageTexture(imagePath) {
    return new Promise((resolve, reject) => {
        const loader = new THREE.TextureLoader();
        loader.load(
            `./img/${imagePath}`,
            (texture) => {
                texture.minFilter = THREE.LinearFilter;
                texture.magFilter = THREE.LinearFilter;
                texture.anisotropy = 16;
                resolve(texture);
            },
            undefined,
            (error) => {
                console.warn(`Failed to load image: ${imagePath}`, error);
                reject(error);
            }
        );
    });
}

function createImageMesh(scene, texture, imageConfig, RAPIER) {
    const geometry = new THREE.PlaneGeometry(imageConfig.width, imageConfig.height);
    
    const material = new THREE.MeshStandardMaterial({
        map: texture,
        side: THREE.DoubleSide,
        metalness: 0.1,
        roughness: 0.8,
        transparent: imageConfig.file.endsWith('.png') || imageConfig.file.endsWith('.svg'),
        emissive: imageConfig.emissiveColor || 0x000000,
        emissiveIntensity: imageConfig.emissiveIntensityNormal || 0
    });
    
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(imageConfig.pos[0], imageConfig.pos[1], imageConfig.pos[2]);
    mesh.rotation.set(imageConfig.rot[0], imageConfig.rot[1], imageConfig.rot[2]);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    scene.add(mesh);
    
    const quaternion = new THREE.Quaternion();
    quaternion.setFromEuler(mesh.rotation);
    
    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(imageConfig.pos[0], imageConfig.pos[1], imageConfig.pos[2])
        .setRotation({ x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w })
        .setLinearDamping(0.5)
        .setAngularDamping(0.8)
        .setCanSleep(false);
    
    const body = physicsWorld.createRigidBody(bodyDesc);
    body.userData = {
    type: 'image',
    meshReference: mesh
};
    
    const colliderDesc = RAPIER.ColliderDesc.cuboid(
        imageConfig.width / 2,
        imageConfig.height / 2,
        imageConfig.depth / 2
    )
        .setRestitution(0.3)
        .setFriction(0.6)
        .setDensity(0.5);
    
    physicsWorld.createCollider(colliderDesc, body);
    
    mesh.userData.physicsBody = body;
    mesh.userData.isImage = true;
    mesh.userData.imageWidth = imageConfig.width;
    mesh.userData.imageHeight = imageConfig.height;
    mesh.userData.imagePath = imageConfig.file;
    mesh.userData.emissiveColor = imageConfig.emissiveColor || 0x000000;
    mesh.userData.emissiveIntensityNormal = imageConfig.emissiveIntensityNormal || 0;
    mesh.userData.emissiveIntensityHit = 1.5;
    mesh.userData.hitTime = 0;
    mesh.userData.isHit = false;
    
    rigidBodies.push(mesh);
    imageObjects.push(mesh);
    
    return mesh;
}

function arrangeImagesInPattern(configs, pattern = 'circle', spacing = 80) {
    const count = configs.length;
    
    if (pattern === 'circle') {
        const radius = spacing;
        const centerX = 0;
        const centerZ = -300;
        
        for (let i = 0; i < count; i++) {
            const angle = (i / count) * Math.PI * 2;
            configs[i].pos = [
                centerX + Math.cos(angle) * radius,
                30,
                centerZ + Math.sin(angle) * radius
            ];
            configs[i].rot = [0, -angle + Math.PI / 2, 0];
        }
    } else if (pattern === 'line') {
        const centerX = -(count - 1) * spacing / 2;
        
        for (let i = 0; i < count; i++) {
            configs[i].pos = [
                centerX + i * spacing,
                30,
                -300
            ];
            configs[i].rot = [0, 0, 0];
        }
    } else if (pattern === 'grid') {
        const cols = Math.ceil(Math.sqrt(count));
        const rows = Math.ceil(count / cols);
        const startX = -(cols - 1) * spacing / 2;
        const startZ = -300 - (rows - 1) * spacing / 2;
        
        for (let i = 0; i < count; i++) {
            const row = Math.floor(i / cols);
            const col = i % cols;
            
            configs[i].pos = [
                startX + col * spacing,
                30,
                startZ + row * spacing
            ];
            configs[i].rot = [0, 0, 0];
        }
    }
}

export async function loadAndCreateImageObjects(scene) {
    const RAPIER = getRapier();
    
    if (config.images.autoArrange) {
        arrangeImagesInPattern(
            imageConfigs, 
            config.images.arrayType, 
            config.images.arraySpacing
        );
    }
    
    for (const imageConfig of imageConfigs) {
        try {
            const texture = await createImageTexture(imageConfig.file);
            createImageMesh(scene, texture, imageConfig, RAPIER);
        } catch (error) {
            console.warn(`Failed to create image object: ${imageConfig.file}`);
        }
    }
}

export function getImageObjects() {
    return imageObjects;
}

export function triggerImageHit(imageObject) {
    if (!imageObject || !imageObject.userData.isImage) return;
    
    imageObject.userData.isHit = true;
    imageObject.userData.hitTime = performance.now();
    
    if (imageObject.material) {
        imageObject.material.emissiveIntensity = imageObject.userData.emissiveIntensityHit || 1.5;
    }
}

export function updateImageEmissions() {
    const currentTime = performance.now();
    
    imageObjects.forEach(obj => {
        if (obj.userData.isHit && obj.material) {
            const elapsed = (currentTime - obj.userData.hitTime) / 1000;
            const fadeDuration = 1.0;
            
            if (elapsed < fadeDuration) {
                const progress = elapsed / fadeDuration;
                const normalIntensity = obj.userData.emissiveIntensityNormal || 0;
                const hitIntensity = obj.userData.emissiveIntensityHit || 1.5;
                const intensity = hitIntensity + (normalIntensity - hitIntensity) * progress;
                
                obj.material.emissiveIntensity = intensity;
            } else {
                obj.userData.isHit = false;
                obj.material.emissiveIntensity = obj.userData.emissiveIntensityNormal || 0;
            }
        }
    });
}